import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  Switch,
  useRouteMatch,
  Route,
  Redirect,
  useParams,
  useLocation,
  useHistory,
} from 'react-router-dom';

import {
  clearCandidates,
  clearPortfolioById,
  getPortfolioById,
  saveFullPortfolio,
} from '../../data/store/portfolio/portfolioActions';

import { Button, Col, Form, Layout, Row } from 'antd';
import ModalUnsaveData from '../../components/ModalUnsaveData/ModalUnsaveData';

import { ReactComponent as Pluse } from '../../assets/cross-btn-icon.svg';
import PortfolioComposeEditInf from './PortfolioComposeEditInf';
import PortfolioCompose from './PortfolioCompose';

const { Header } = Layout;

const PortfolioComposeRoute = ({
  userId,
  needSave,
  setNeedSave,
  saveAlert,
  setSaveAlert,
  theme,
}) => {
  const { portfolioId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation('');
  const { pathname } = useLocation();
  const { url } = useRouteMatch();
  const [formInfo] = Form.useForm();

  const {
    isLoading,
    portfolioById,
    portfolioList: { list: portfolioList },
  } = useSelector((state) => state.portfolioReducer);

  const [selectedKeys, setSelectedKeys] = useState([]);

  const [data, setData] = useState([]);
  const [dataUnlisted, setDataUnlisted] = useState([]);
  const [dataCashes, setDataCashes] = useState([]);
  const [totalValue, setTotalValue] = useState(0);

  const [modalCancelVisible, setModalCancelVisible] = useState(false);
  const [needSaveInfo, setNeedSaveInfo] = useState(false);

  const getPortfolio = (id) => {
    return portfolioList?.find((portfolio) => portfolio.id === id);
  };

  useEffect(() => {
    return () => {
      dispatch(clearCandidates());
    };
  }, []);

  useEffect(() => {
    const areEqual =
      _.xor(selectedKeys, portfolioById?.composites).length === 0;
    setNeedSave(needSaveInfo || !areEqual);
  }, [needSaveInfo, selectedKeys, portfolioById]);

  useEffect(() => {
    if (portfolioId !== 'create') {
      dispatch(getPortfolioById({ portfolioId }));
    } else {
      dispatch(clearPortfolioById());
    }
  }, [portfolioId]);

  useEffect(() => {
    if (portfolioById && Object.keys(portfolioById).length) {
      setSelectedKeys(portfolioById?.composites ?? []);
    } else {
      setSelectedKeys([]);
    }
  }, [portfolioById]);

  useEffect(() => {
    let accumulatedData = [];
    let accumulatedUnlisted = [];
    let accumulatedCashes = [];
    let accumulatedTotalValue = 0;
    if (selectedKeys?.length) {
      for (const key of selectedKeys) {
        const portfolio = getPortfolio(key);
        if (portfolio) {
          accumulatedData = accumulatedData.concat(portfolio.portfolio_content);
          accumulatedUnlisted = accumulatedUnlisted.concat(
            portfolio.unlisted_content,
          );
          accumulatedCashes = accumulatedCashes.concat(
            portfolio.portfolio_cashes,
          );
          accumulatedTotalValue = accumulatedTotalValue + portfolio.total_value;
        }
      }
    }
    setData(accumulatedData);
    setDataUnlisted(accumulatedUnlisted);
    setDataCashes(accumulatedCashes);
    setTotalValue(accumulatedTotalValue);
  }, [selectedKeys, portfolioList]);

  useEffect(() => {
    saveAlert && saveAlert.flag && setModalCancelVisible(true);
  }, [saveAlert]);

  const suffix = useMemo(() => {
    return pathname.split('/').at(-1);
  }, [pathname]);

  const handleTab = (path) => {
    if (needSave) {
      setSaveAlert({ flag: true, path });
    } else {
      history.push(`${url}/${path}`);
    }
  };

  const handleCancel = () => {
    setNeedSave(false);
    setSaveAlert({ flag: false, path: '' });
    history.push(`/accounts/${userId}/portfolio`);
  };

  const closeModal = () => {
    setModalCancelVisible(false);
  };

  const cancelWithoutChange = () => {
    const path = saveAlert.path;
    setNeedSave(false);
    setSaveAlert({ flag: false, path: '' });
    dispatch(getPortfolioById({ portfolioId }));
    closeModal();
    history.push(path);
  };

  const handleSubmit = async () => {
    try {
      const data = await formInfo.validateFields();

      const newPortfolio = {
        data: {
          account_id: +userId,
          ...data,
          composites: selectedKeys,
          is_composite: true,
        },
        history,
        userId,
        portfolioId: portfolioId === 'create' ? 'create' : portfolioById?.id,
        path: saveAlert.path || `/accounts/${userId}/portfolio`,
        modalClose: closeModal,
      };
      dispatch(saveFullPortfolio(newPortfolio));
    } catch (error) {
      console.error('Validation error:', error);
      return;
    }
    setNeedSaveInfo(false);
    setNeedSave(false);
    setSaveAlert({ flag: false, path: '' });
    closeModal();
  };

  return (
    <>
      <div className="portfolio-wrapper">
        <Header className="headerStyle">
          <Row className="overview-tabs-wrapper">
            <Col className="tabs-tab-wrapper" span={18}>
              <Button
                type="text"
                className={`overview-btn ${suffix === 'holdings' ? 'ant-btn-active' : ''}`}
                onClick={() => handleTab('holdings')}
              >
                {t('HOLDINGS')}
              </Button>
            </Col>
            <Col className="tabs-btn-wrapper" span={6}>
              <Button
                type="text"
                className="portfolio-cancel-btn"
                icon={<Pluse />}
                iconPosition="end"
                onClick={handleCancel}
              >
                {t('CANCEL')}
              </Button>
              <Button
                type="default"
                className="portfolio-save-btn"
                onClick={handleSubmit}
                disabled={!needSave || isLoading}
              >
                {t('SAVE')}
              </Button>
            </Col>
          </Row>
        </Header>
        <div className="porfolio-info-actions-wrapper composite">
          <div className="porfolio-info-wrapper">
            <PortfolioComposeEditInf
              form={formInfo}
              userId={userId}
              portfolioId={portfolioId}
              setNeedSaveInfo={setNeedSaveInfo}
              saveAlert={saveAlert}
              setSaveAlert={setSaveAlert}
              theme={theme}
              totalValue={totalValue}
            />
          </div>
          <div className="porfolio-actions-wrapper">
            <Switch>
              <Route exact path={`${url}/includes`}>
                <PortfolioCompose
                  userId={userId}
                  portfolioId={portfolioId}
                  saveAlert={saveAlert}
                  setSaveAlert={setSaveAlert}
                  data={data}
                  dataUnlisted={dataUnlisted}
                  dataCashes={dataCashes}
                  setData={setData}
                  setDataUnlisted={setDataUnlisted}
                  setDataCashes={setDataCashes}
                  selectedKeys={selectedKeys}
                  setSelectedKeys={setSelectedKeys}
                  totalValue={totalValue}
                />
              </Route>
              <Route exact path="*">
                <Redirect to={`${url}/includes`} />
              </Route>
            </Switch>
            <ModalUnsaveData
              open={modalCancelVisible}
              closeModal={closeModal}
              cancelWithoutChange={cancelWithoutChange}
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PortfolioComposeRoute;

PortfolioComposeRoute.propTypes = {
  userId: PropTypes.string,
  needSave: PropTypes.bool,
  setNeedSave: PropTypes.func,
  saveAlert: PropTypes.object,
  setSaveAlert: PropTypes.func,
  theme: PropTypes.string,
};
